/*!
 * AGL DLS Components built with Stencil
 */
import { Component, Prop, h, Host, Event, Watch, Element } from '@stencil/core';
import { convertIcon, generateRandomNumber, hideFocusRingWhenUsingMouse, validateProp } from '../../utils/utils';
import { getHintText, getAlertText } from '../../common/forms/forms';
import iconChevronFromFile from '../../assets/functional/utility/chevron-down.svg';
/**
 * Class Dropdown
 */
export class Dropdown {
  constructor() {
    /* Props */
    /**
     *  Unique ID for the dropdown and for binding the label to the dropdown
     */
    this.dropdownId = generateRandomNumber();
    /**
     * Sets the field as being optional. All fields are considered required unless this prop equals true
     */
    this.optional = false;
    /**
     * Shows if the dropdown is invalid
     */
    this.invalid = false;
    /**
     * Determines the over-ride top margin size
     */
    this.topMargin = 'none';
  }
  init() {
    const dropDownOptions = this.host.querySelectorAll('option');
    if ((dropDownOptions === null || dropDownOptions === void 0 ? void 0 : dropDownOptions.length) > 0) {
      for (let y = 0; y < dropDownOptions.length; y++) {
        const option = dropDownOptions[y];
        if (this.defaultValue && option) {
          if (option.value === this.defaultValue) {
            option.setAttribute('selected', 'true');
          }
          else {
            option.removeAttribute('selected');
          }
        }
      }
    }
  }
  /* Getters */
  get getAttributes() {
    const { optional, hintText, dropdownId, invalid } = this;
    const attributes = {};
    if (!optional) {
      attributes['required'] = true;
    }
    let ariaDescribedBy = '';
    if (hintText) {
      ariaDescribedBy = 'hint-text-' + dropdownId + ' ';
    }
    if (invalid) {
      ariaDescribedBy += ' alert-text-' + dropdownId;
      attributes['aria-invalid'] = true;
    }
    if (ariaDescribedBy) {
      attributes['aria-describedby'] = ariaDescribedBy;
    }
    return attributes;
  }
  get getClasses() {
    return {
      ['dls-dropdown']: true,
      [`dls-dropdown--error`]: this.invalid
    };
  }
  getLabel(label, optional, dropdownId, topMargin) {
    return (label && (h("label", { class: { ['dls-top-margin--' + topMargin]: !!topMargin }, htmlFor: dropdownId },
      label,
      " ",
      optional && ' (optional)')));
  }
  // propagate value change from view to model
  inputChanged(ev) {
    if (ev.target && ev.target.value) {
      this.selectedValue.emit(ev.target.value); // emit val for the checkedChange event
    }
  }
  componentWillLoad() {
    const { label } = this;
    validateProp('label', label, null, true);
  }
  componentDidLoad() {
    this.init();
    hideFocusRingWhenUsingMouse(this.selectElement);
    // loading in icon from file - to set dynamically - using the icon asset - and utilising the DLS token and accommodate for light and dark mode
    let convertedIcon = convertIcon(iconChevronFromFile, '--colors-primary-01', this.host);
    convertedIcon = convertedIcon.replace(/"/g, '\\"').replace(/#/g, '%23').replace(/\n/g, '').replace(/\r/g, '');
    const convertedURL = `url("data:image/svg+xml;utf8,${convertedIcon}")`;
    this.selectElement.style.backgroundImage = convertedURL;
  }
  /* Render */
  render() {
    const { getClasses, getAttributes, label, hintText, errorText, invalid, optional, dropdownId, topMargin, getLabel, defaultValue } = this;
    return (h(Host, null,
      getLabel(label, optional, dropdownId, topMargin),
      getHintText(hintText, dropdownId),
      h("select", Object.assign({ id: dropdownId, ref: (l) => (this.selectElement = l), class: getClasses, "aria-invalid": this.invalid }, getAttributes, { onChange: (event) => this.inputChanged(event) }),
        !defaultValue && h("option", { value: "", selected: true, disabled: !optional }),
        h("slot", null)),
      getAlertText(errorText, null, invalid, dropdownId)));
  }
  static get is() { return "dls-dropdown"; }
  static get encapsulation() { return "scoped"; }
  static get originalStyleUrls() { return {
    "$": ["_index.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["_index.css"]
  }; }
  static get properties() { return {
    "dropdownId": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Unique ID for the dropdown and for binding the label to the dropdown"
      },
      "attribute": "dropdown-id",
      "reflect": false,
      "defaultValue": "generateRandomNumber()"
    },
    "label": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The label for the Dropdown."
      },
      "attribute": "label",
      "reflect": false
    },
    "optional": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets the field as being optional. All fields are considered required unless this prop equals true"
      },
      "attribute": "optional",
      "reflect": false,
      "defaultValue": "false"
    },
    "invalid": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Shows if the dropdown is invalid"
      },
      "attribute": "invalid",
      "reflect": false,
      "defaultValue": "false"
    },
    "hintText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The hint text to be displayed"
      },
      "attribute": "hint-text",
      "reflect": false
    },
    "errorText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The text to be displayed when the dropdown is in an error state and the invalid prop is equal to true"
      },
      "attribute": "error-text",
      "reflect": false
    },
    "defaultValue": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets the default value of the dropdown, if a value is not set, a placeholder option is created and that will be set as\r\ndefault - (if this is the case AND the optional prop is set to false the placeholder value will be disabled)"
      },
      "attribute": "default-value",
      "reflect": false
    },
    "topMargin": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "TopMarginType",
        "resolved": "\"none\" | \"space00\" | \"space01\" | \"space02\" | \"space03\" | \"space04\" | \"space05\" | \"space06\"",
        "references": {
          "TopMarginType": {
            "location": "import",
            "path": "../../types/component.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines the over-ride top margin size"
      },
      "attribute": "top-margin",
      "reflect": false,
      "defaultValue": "'none'"
    }
  }; }
  static get events() { return [{
      "method": "selectedValue",
      "name": "selectedValue",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Dropdown option selected event"
      },
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      }
    }]; }
  static get elementRef() { return "host"; }
  static get watchers() { return [{
      "propName": "defaultValue",
      "methodName": "init"
    }]; }
}
